import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { RefreshCw } from 'lucide-react';
import {
  costService, CostSummary, OrderCostRow, SearchCostRow, SessionCostRow, RawCostRow, PricingTable,
} from '../services/CostService';

const PRICING_LS_KEY = 'llm_cost_pricing_v1';
const ORDER_PAGE_SIZE = 100;

const ACCENT = '#6866ff';
const PRIORITY = '#ef6f6f';
const BORDER = '#efebe2';

const usd = (n: number | null | undefined) =>
  '$' + (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const num = (n: number | null | undefined) => Number(n || 0).toLocaleString();

type TableTab = 'sessions' | 'orders' | 'searches' | 'rows';
type Drill = { type: 'order'; id: number } | { type: 'search'; id: string } | { type: 'session'; id: string } | null;

const Tile: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
    <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
    {sub && <p className="text-[11px] text-[#999999] mt-0.5">{sub}</p>}
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode; right?: React.ReactNode }> = ({ title, children, right }) => (
  <div className="bg-white border rounded-xl p-4" style={{ borderColor: BORDER }}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-[#201e1a]">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const LLMCostDashboard: React.FC = () => {
  const [days, setDays] = useState(30);
  const [scopeAll, setScopeAll] = useState(true);   // true = all orgs, false = my org only
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [orders, setOrders] = useState<OrderCostRow[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [orderPage, setOrderPage] = useState(0);
  const [searches, setSearches] = useState<SearchCostRow[]>([]);
  const [sessions, setSessions] = useState<SessionCostRow[]>([]);
  const [rawRows, setRawRows] = useState<RawCostRow[]>([]);
  const [tab, setTab] = useState<TableTab>('sessions');
  const [drill, setDrill] = useState<Drill>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);   // bump to force a re-fetch

  // Editable price table (UI-only): `pricing` is what queries use; `draft` is
  // the in-editor copy. Seeded from localStorage, else the backend defaults.
  const [pricing, setPricing] = useState<PricingTable | null>(null);
  const [draft, setDraft] = useState<PricingTable | null>(null);
  const [showPricing, setShowPricing] = useState(false);

  useEffect(() => {
    (async () => {
      let p: PricingTable | null = null;
      try { const saved = localStorage.getItem(PRICING_LS_KEY); if (saved) p = JSON.parse(saved); } catch { /* ignore */ }
      if (!p) { try { p = await costService.getDefaultPricing(); } catch { /* leave null → backend defaults */ } }
      setPricing(p); setDraft(p);
    })();
  }, []);

  const pricingKey = pricing ? JSON.stringify(pricing) : 'default';

  // Load headline + tables whenever the window or applied prices change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const [s, se, ss] = await Promise.all([
          costService.getSummary({ days, all: scopeAll, pricing: pricing || undefined }),
          costService.getSearches({ days, all: scopeAll, limit: 100, pricing: pricing || undefined }),
          costService.getSessions({ days, all: scopeAll, limit: 200, pricing: pricing || undefined }),
        ]);
        if (cancelled) return;
        setSummary(s); setSearches(se); setSessions(ss);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load cost data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, scopeAll, pricingKey, refreshKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Orders are driven by the order-management list itself (all time, paginated),
  // NOT by the time window — so every order shows up here with its cost, even
  // orders that have $0 attributed LLM spend. Independent of the `days` selector.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { rows, total_count } = await costService.getOrders({
          all: scopeAll, limit: ORDER_PAGE_SIZE, offset: orderPage * ORDER_PAGE_SIZE,
          pricing: pricing || undefined,
        });
        if (cancelled) return;
        setOrders(rows); setOrdersTotal(total_count);
      } catch { /* leave previous rows in place */ }
    })();
    return () => { cancelled = true; };
  }, [scopeAll, pricingKey, refreshKey, orderPage]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Reset to the first page when the org scope changes.
  useEffect(() => { setOrderPage(0); }, [scopeAll]);

  // Load raw rows on demand (drill-down or the "rows" tab)
  useEffect(() => {
    if (tab !== 'rows' && !drill) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await costService.getRows({
          days, all: scopeAll, limit: 300, pricing: pricing || undefined,
          order_id: drill?.type === 'order' ? drill.id : undefined,
          search_id: drill?.type === 'search' ? drill.id : undefined,
          session_id: drill?.type === 'session' ? drill.id : undefined,
        });
        if (!cancelled) setRawRows(rows);
      } catch { /* table just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [tab, drill, days, scopeAll, pricingKey, refreshKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const applyPricing = () => {
    if (!draft) return;
    setPricing(draft);
    try { localStorage.setItem(PRICING_LS_KEY, JSON.stringify(draft)); } catch { /* ignore */ }
    setShowPricing(false);
  };
  const resetPricing = async () => {
    try {
      const def = await costService.getDefaultPricing();
      setDraft(def); setPricing(def);
      localStorage.removeItem(PRICING_LS_KEY);
    } catch { /* ignore */ }
  };
  const editRate = (model: string, field: keyof PricingTable['models'][string], val: number) =>
    setDraft((d) => d && ({ ...d, models: { ...d.models, [model]: { ...d.models[model], [field]: val } } }));

  const h = summary?.headline;

  // top agents/models for the breakdown bar chart
  const breakdownData = useMemo(() => {
    const map: Record<string, { name: string; standard: number; priority: number }> = {};
    (summary?.breakdown || []).forEach((r) => {
      const key = `${r.agent || '?'} · ${r.model || '?'}`;
      map[key] = map[key] || { name: key, standard: 0, priority: 0 };
      if (r.tier === 'priority') map[key].priority += r.cost_usd;
      else map[key].standard += r.cost_usd;
    });
    return Object.values(map).sort((a, b) => (b.standard + b.priority) - (a.standard + a.priority)).slice(0, 8);
  }, [summary]);

  const priorityPct = h && h.total_usd ? Math.round((h.priority_usd / h.total_usd) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* header + window selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#201e1a]">LLM Cost</h2>
          <p className="text-xs text-[#999999]">Per-order and per-part-search spend, attributed from token usage.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-[#f6f4f0] rounded-lg p-1">
            {[{ k: true, l: 'All orgs' }, { k: false, l: 'My org' }].map(({ k, l }) => (
              <button key={l} onClick={() => setScopeAll(k)}
                className={`px-3 py-1 text-xs rounded-md font-medium ${scopeAll === k ? 'bg-white text-[#201e1a] shadow-sm' : 'text-[#999999]'}`}>
                {l}
              </button>
            ))}
          </div>
          <button onClick={() => setShowPricing((s) => !s)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium border ${showPricing ? 'bg-[#6866ff] text-white border-[#6866ff]' : 'text-[#6866ff] border-[#dcdbff] bg-white'}`}>
            Edit prices
          </button>
          <button onClick={() => setRefreshKey((k) => k + 1)} disabled={loading}
            title="Refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium border text-[#201e1a] border-[#efebe2] bg-white hover:bg-[#f6f4f0] disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <div className="flex gap-1 bg-[#f6f4f0] rounded-lg p-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-3 py-1 text-xs rounded-md font-medium ${days === d ? 'bg-white text-[#201e1a] shadow-sm' : 'text-[#999999]'}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Editable price table (UI-only; persisted in this browser) */}
      {showPricing && draft && (
        <Card title="Pricing — USD per 1M tokens (standard tier)" right={
          <div className="flex items-center gap-2 text-xs">
            <button onClick={resetPricing} className="text-[#999999] hover:text-[#201e1a]">Reset to default</button>
            <button onClick={applyPricing} className="px-3 py-1 rounded-md bg-[#6866ff] text-white font-medium">Apply</button>
          </div>
        }>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[#999999] text-xs uppercase tracking-wide">
                  <th className="px-2 py-2 text-left">Model</th>
                  <th className="px-2 py-2 text-right">Input</th>
                  <th className="px-2 py-2 text-right">Cached</th>
                  <th className="px-2 py-2 text-right">Output</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(draft.models).map(([model, r]) => (
                  <tr key={model} className="border-t" style={{ borderColor: BORDER }}>
                    <td className="px-2 py-1.5 text-[#201e1a] font-medium whitespace-nowrap">{model}</td>
                    {(['in', 'cached', 'out'] as const).map((f) => (
                      <td key={f} className="px-2 py-1.5 text-right">
                        <input type="number" step="0.01" min="0" value={(r as any)[f]}
                          onChange={(e) => editRate(model, f as any, parseFloat(e.target.value) || 0)}
                          className="w-20 text-right border rounded px-2 py-1 text-[#201e1a]" style={{ borderColor: BORDER }} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 mt-3 text-sm">
            <span className="text-[#999999]">Priority multiplier</span>
            <input type="number" step="0.1" min="1" value={draft.priority_mult}
              onChange={(e) => setDraft((d) => d && ({ ...d, priority_mult: parseFloat(e.target.value) || 1 }))}
              className="w-20 text-right border rounded px-2 py-1 text-[#201e1a]" style={{ borderColor: BORDER }} />
            <span className="text-[#999999] text-xs">× standard rate for priority-tier calls. Changes re-cost all history on Apply.</span>
          </div>
        </Card>
      )}

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>}
      {loading && <div className="text-sm text-[#999999] py-8 text-center">Loading cost data…</div>}

      {!loading && !error && (
        <>
          {/* KPI tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Tile label="Total spend" value={usd(h?.total_usd)} sub={`${num(h?.llm_calls)} calls`} />
            <Tile label="Avg / order" value={usd(h?.avg_usd_per_order)} sub={`${num(h?.orders)} orders`} />
            <Tile label="Avg / search" value={usd(h?.avg_usd_per_search)} sub={`${num(h?.searches)} searches`} />
            <Tile label="Priority spend" value={usd(h?.priority_usd)} sub={`${priorityPct}% of total`} />
            <Tile label="Standard spend" value={usd(h?.standard_usd)} sub={`${100 - priorityPct}% of total`} />
          </div>

          {/* charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card title="Daily spend">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={summary?.daily || []} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0efea" />
                  <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#999' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#999' }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => '$' + v} />
                  <Tooltip formatter={(v: any) => usd(Number(v) || 0)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="cost_usd" name="Total" stroke={ACCENT} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="priority_usd" name="Priority" stroke={PRIORITY} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card title="Cost by agent · model (priority vs standard)">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={breakdownData} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }} barCategoryGap="25%">
                  <XAxis type="number" tick={{ fontSize: 9, fill: '#999' }} axisLine={false} tickLine={false} tickFormatter={(v) => '$' + v} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 9, fill: '#777' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v: any) => usd(Number(v) || 0)} cursor={{ fill: 'rgba(32,30,26,0.04)' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="standard" name="Standard" stackId="a" fill={ACCENT} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="priority" name="Priority" stackId="a" fill={PRIORITY} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* drill-down banner */}
          {drill && (
            <div className="flex items-center justify-between bg-[#f0efff] border border-[#dcdbff] rounded-lg px-4 py-2 text-sm">
              <span className="text-[#201e1a]">
                Showing raw calls for {drill.type === 'order' ? `order #${drill.id}` : drill.type === 'session' ? `session ${drill.id}` : `search ${drill.id}`}
              </span>
              <button onClick={() => { setDrill(null); setTab('sessions'); }} className="text-[#6866ff] font-medium">Clear ✕</button>
            </div>
          )}

          {/* tables */}
          <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: BORDER }}>
            <div className="flex border-b" style={{ borderColor: BORDER }}>
              {(['sessions', 'orders', 'searches', 'rows'] as TableTab[]).map((tk) => (
                <button key={tk} onClick={() => { setTab(tk); if (tk !== 'rows') setDrill(null); }}
                  className={`px-4 py-2.5 text-sm font-medium capitalize ${tab === tk ? 'text-[#6866ff] border-b-2 border-[#6866ff]' : 'text-[#999999]'}`}>
                  {tk === 'rows' ? 'Raw calls' : tk}
                </button>
              ))}
            </div>

            {tab === 'sessions' && (
              <Table head={['Session', 'Cost', 'Priority', 'Calls', 'Orders', 'Searches', 'In / Cached / Out tok', 'Last activity']}
                rows={sessions.map((s) => [
                  <button className="text-[#6866ff] font-medium" onClick={() => { setDrill({ type: 'session', id: s.session_id }); setTab('rows'); }}>{s.session_id}</button>,
                  usd(s.session_cost_usd), usd(s.priority_cost_usd), num(s.llm_calls), num(s.orders), num(s.searches),
                  `${num(s.input_tok)} / ${num(s.cached_tok)} / ${num(s.output_tok)}`,
                  s.last_activity?.slice(0, 16)?.replace('T', ' ') || '—',
                ])} empty="No tracked sessions in this window." />
            )}

            {tab === 'orders' && (
              <>
                <Table head={['Order', 'Status', 'Cost', 'Priority', 'Calls', 'In / Cached / Out tok', 'Last activity']}
                  rows={orders.map((o) => [
                    <button className="text-[#6866ff] font-medium text-left" onClick={() => { setDrill({ type: 'order', id: o.order_id }); setTab('rows'); }}>
                      #{o.order_id}{o.order_name ? ` · ${o.order_name}` : ''}
                    </button>,
                    <span className="text-[#999999]">{o.status || '—'}</span>,
                    o.has_cost ? usd(o.order_cost_usd) : <span className="text-[#bbbbbb]">$0.00</span>,
                    usd(o.priority_cost_usd), num(o.llm_calls),
                    `${num(o.input_tok)} / ${num(o.cached_tok)} / ${num(o.output_tok)}`,
                    o.last_activity?.slice(0, 16)?.replace('T', ' ') || '—',
                  ])} empty="No orders found for this scope." />
                <div className="flex items-center justify-between px-4 py-2.5 border-t text-xs text-[#999999]" style={{ borderColor: BORDER }}>
                  <span>
                    {ordersTotal > 0
                      ? `${orderPage * ORDER_PAGE_SIZE + 1}–${Math.min((orderPage + 1) * ORDER_PAGE_SIZE, ordersTotal)} of ${num(ordersTotal)} orders`
                      : 'No orders'}
                  </span>
                  <div className="flex gap-1">
                    <button disabled={orderPage === 0} onClick={() => setOrderPage((p) => Math.max(0, p - 1))}
                      className="px-3 py-1 rounded-md border bg-white disabled:opacity-40" style={{ borderColor: BORDER }}>Prev</button>
                    <button disabled={(orderPage + 1) * ORDER_PAGE_SIZE >= ordersTotal} onClick={() => setOrderPage((p) => p + 1)}
                      className="px-3 py-1 rounded-md border bg-white disabled:opacity-40" style={{ borderColor: BORDER }}>Next</button>
                  </div>
                </div>
              </>
            )}

            {tab === 'searches' && (
              <Table head={['Search (turn)', 'Order', 'Parts', 'Cost', 'Cost / part', 'Calls']}
                rows={searches.map((s) => [
                  <button className="text-[#6866ff] font-medium" onClick={() => { setDrill({ type: 'search', id: s.search_id }); setTab('rows'); }}>{s.search_id}</button>,
                  s.order_id != null ? `#${s.order_id}` : '—', num(s.parts),
                  usd(s.search_cost_usd), usd(s.cost_per_part), num(s.llm_calls),
                ])} empty="No part-search cost in this window." />
            )}

            {tab === 'rows' && (
              <Table head={['Time', 'Agent', 'Model', 'Tier', 'Order', 'Search', 'In/Cache/Out', 'Cost']}
                rows={rawRows.map((r) => [
                  r.ts?.slice(0, 19)?.replace('T', ' ') || '—', r.agent || '—', r.model || '—',
                  <span className={r.tier === 'priority' ? 'text-[#ef6f6f] font-medium' : 'text-[#999999]'}>{r.tier}</span>,
                  r.order_id != null ? `#${r.order_id}` : '—', r.search_id ? '✓' : '—',
                  `${num(r.input_tok)}/${num(r.cached_tok)}/${num(r.output_tok)}`, usd(r.cost_usd),
                ])} empty="Pick an order or search above, or no calls in this window." />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const Table: React.FC<{ head: string[]; rows: React.ReactNode[][]; empty: string }> = ({ head, rows, empty }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-[#f6f4f0] text-[#999999] text-xs uppercase tracking-wide">
          {head.map((hd, i) => <th key={i} className="px-4 py-3 text-left whitespace-nowrap">{hd}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y" style={{ borderColor: BORDER }}>
        {rows.length === 0 && (
          <tr><td colSpan={head.length} className="px-4 py-8 text-center text-[#999999]">{empty}</td></tr>
        )}
        {rows.map((cells, ri) => (
          <tr key={ri} className="hover:bg-[#f6f4f0] transition-colors">
            {cells.map((c, ci) => <td key={ci} className="px-4 py-2.5 text-[#201e1a] whitespace-nowrap">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default LLMCostDashboard;
